<template>
  <div
    :data-section-key="block.section?.key"
    class="card overflow-hidden"
    :class="{ 'opacity-60': !block.visible }">
    <header class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <span
        class="section-card-handle cursor-grab text-gray-400 hover:text-gray-600 select-none mr-2"
        title="Drag to reorder"
        aria-hidden="true"><EditorIcon name="drag" class="w-4 h-4" /></span>
      <button
        type="button"
        class="flex items-center gap-2 text-left flex-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        :aria-expanded="open"
        :aria-controls="bodyId"
        @click="open = !open">
        <EditorIcon :name="open ? 'chevron-down' : 'chevron-right'" class="w-4 h-4 text-gray-500" />
        <span class="font-medium text-gray-900">{{ block.section?.name || block.section?.key }}</span>
        <span v-if="block.section?.repeatable" class="badge badge-default badge-sm">
          {{ itemCount }} / {{ block.section.max_items }}
        </span>
      </button>

      <div class="flex items-center gap-0.5">
        <!-- Keyboard-accessible reorder (drag is mouse-only). -->
        <button
          type="button"
          class="btn btn-ghost btn-icon btn-xs"
          title="Move section up"
          aria-label="Move section up"
          :disabled="isFirst"
          @click.stop="$emit('move', -1)">
          <EditorIcon name="arrow-up" class="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          class="btn btn-ghost btn-icon btn-xs"
          title="Move section down"
          aria-label="Move section down"
          :disabled="isLast"
          @click.stop="$emit('move', 1)">
          <EditorIcon name="arrow-down" class="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          class="btn btn-ghost btn-icon btn-xs"
          title="View edit history for this section"
          aria-label="View edit history for this section"
          @click.stop="$emit('open-history')">
          <EditorIcon name="history" class="w-3.5 h-3.5" />
        </button>
        <button
          v-if="block.section?.duplicatable"
          type="button"
          class="btn btn-ghost btn-icon btn-xs"
          title="Duplicate this section"
          aria-label="Duplicate section"
          @click.stop="$emit('duplicate')">
          <EditorIcon name="duplicate" class="w-3.5 h-3.5" />
        </button>
        <button
          v-if="block.section?.removable"
          type="button"
          class="btn btn-ghost btn-icon btn-xs text-gray-500 hover:text-red-600"
          title="Delete this section"
          aria-label="Delete section"
          @click.stop="$emit('delete')">
          <EditorIcon name="trash" class="w-3.5 h-3.5" />
        </button>
        <!-- Visibility toggle — icon-free; label is conveyed via tooltip + a11y text. -->
        <label
          v-if="block.section?.togglable"
          class="inline-flex items-center ml-1"
          :title="block.visible ? 'Visible — click to hide this section' : 'Hidden — click to show this section'">
          <input
            type="checkbox"
            :checked="block.visible"
            class="form-toggle"
            @change="$emit('toggle', $event.target.checked)">
          <span class="sr-only">Section visible</span>
        </label>
      </div>
    </header>

    <div v-if="open" :id="bodyId" class="p-4">
      <!-- Repeatable list -->
      <div v-if="block.section?.repeatable">
        <!-- Section-level header copy (eyebrow / heading / intro) — edited once, not per item. -->
        <div v-if="Object.keys(sectionSchema).length" class="mb-4 pb-4 border-b border-gray-100">
          <FieldInput
            v-for="(defn, fieldKey) in sectionSchema"
            :key="fieldKey"
            :field-key="fieldKey"
            :defn="defn"
            :model-value="block.content?.[fieldKey] || ''"
            :alt-value="block.content?.[`${fieldKey}_alt`] || ''"
            :default-value="block.section?.default_content?.[fieldKey] || ''"
            :style-override="fieldStyle(fieldKey)"
            :brand-swatches="brandSwatches"
            :page-links="pageLinks"
            @update:model-value="onFieldUpdate(fieldKey, $event)"
            @update:alt="onFieldUpdate(`${fieldKey}_alt`, $event)"
            @update:style-override="onFieldStyleUpdate(fieldKey, $event)"
            @preview="emitPreview(fieldKey, $event, null)"
            @focus="$emit('field-focus', { section: block.section?.key, field: fieldKey, itemUid: null })"
            @generate="$emit('generate-image', { field: fieldKey, index: null })" />
        </div>
        <p v-if="autoSourced" class="alert alert-info">
          Cards auto-sync from your <strong>{{ autoSourceLabel }}</strong> list. Edit names &amp; descriptions there to update every page.
        </p>
        <div ref="itemList">
          <div
            v-for="(item, index) in items"
            :key="item._uid || index"
            :data-item-uid="item._uid"
            :data-item-index="index"
            class="border border-gray-200 rounded-md p-3 mb-3">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-1.5">
                <span
                  v-if="!autoSourced && item._uid"
                  class="item-card-handle cursor-grab text-gray-400 hover:text-gray-600 select-none"
                  title="Drag to reorder"
                  aria-hidden="true"><EditorIcon name="drag" class="w-4 h-4" /></span>
                <span class="text-overline text-gray-500">Item {{ index + 1 }}</span>
                <!-- Keyboard-accessible item reorder (drag is mouse-only). -->
                <template v-if="!autoSourced && item._uid">
                  <button
                    type="button"
                    class="btn btn-ghost btn-icon btn-xs"
                    title="Move item up"
                    aria-label="Move item up"
                    :disabled="index === 0"
                    @click="moveItem(index, -1)">
                    <EditorIcon name="arrow-up" class="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    class="btn btn-ghost btn-icon btn-xs"
                    title="Move item down"
                    aria-label="Move item down"
                    :disabled="index === items.length - 1"
                    @click="moveItem(index, 1)">
                    <EditorIcon name="arrow-down" class="w-3.5 h-3.5" />
                  </button>
                </template>
              </div>
              <button
                v-if="!autoSourced"
                type="button"
                class="btn btn-link btn-sm text-red-600"
                :disabled="items.length <= (block.section.min_items || 0)"
                @click="$emit('remove-item', index)">
                Remove
              </button>
            </div>
            <FieldInput
              v-for="(defn, fieldKey) in itemSchema"
              :key="fieldKey"
              :field-key="fieldKey"
              :defn="defn"
              :model-value="item[fieldKey] || ''"
              :alt-value="item[`${fieldKey}_alt`] || ''"
              :default-value="defaultItemValue(index, fieldKey)"
              :style-override="itemFieldStyle(item._uid, fieldKey)"
              :brand-swatches="brandSwatches"
              :page-links="pageLinks"
              @update:model-value="onItemFieldUpdate(index, fieldKey, $event)"
              @update:alt="onItemFieldUpdate(index, `${fieldKey}_alt`, $event)"
              @update:style-override="onItemFieldStyleUpdate(item._uid, fieldKey, $event)"
              @preview="emitPreview(fieldKey, $event, item._uid)"
              @focus="$emit('field-focus', { section: block.section?.key, field: fieldKey, itemUid: item._uid })"
              @generate="$emit('generate-image', { field: fieldKey, index })" />
          </div>
        </div>
        <button
          v-if="!autoSourced"
          type="button"
          class="btn btn-secondary btn-sm btn-full"
          :disabled="items.length >= (block.section.max_items || 99)"
          @click="$emit('add-item')">
          + Add item
        </button>
      </div>

      <!-- Static section -->
      <div v-else>
        <FieldInput
          v-for="(defn, fieldKey) in block.section?.schema || {}"
          :key="fieldKey"
          :field-key="fieldKey"
          :defn="defn"
          :model-value="block.content?.[fieldKey] || ''"
          :alt-value="block.content?.[`${fieldKey}_alt`] || ''"
          :default-value="block.section?.default_content?.[fieldKey] || ''"
          :style-override="fieldStyle(fieldKey)"
          :brand-swatches="brandSwatches"
          :page-links="pageLinks"
          @update:model-value="onFieldUpdate(fieldKey, $event)"
          @update:alt="onFieldUpdate(`${fieldKey}_alt`, $event)"
          @update:style-override="onFieldStyleUpdate(fieldKey, $event)"
          @preview="emitPreview(fieldKey, $event, null)"
          @focus="$emit('field-focus', { section: block.section?.key, field: fieldKey, itemUid: null })"
          @generate="$emit('generate-image', { field: fieldKey, index: null })" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Sortable from 'sortablejs'
import FieldInput from './FieldInput.vue'
import EditorIcon from './EditorIcon.vue'

const props = defineProps({
  block: { type: Object, required: true },
  isFirst: { type: Boolean, default: false },
  isLast: { type: Boolean, default: false },
  brandSwatches: { type: Array, default: () => []},
  pageLinks: { type: Array, default: () => []}
})

const emit = defineEmits(['update', 'toggle', 'add-item', 'remove-item', 'reorder-items', 'update-overrides', 'open-history', 'move', 'preview', 'field-focus', 'generate-image', 'duplicate', 'delete'])

// Live preview-only update (no save) as the customer types — the parent
// live-patches the matching preview element instantly. The save still happens
// on blur via the `update` events.
function emitPreview(fieldKey, value, itemUid) {
  emit('preview', { section: props.block.section?.key, field: fieldKey, value, itemUid: itemUid || null })
}

const bodyId = computed(() => `section-body-${props.block.id}`)

const itemList = ref(null)
let itemSortable = null

function mountItemSortable() {
  if (!itemList.value) return
  if (itemSortable) itemSortable.destroy()
  if (!props.block.section?.repeatable || autoSourced.value) return

  itemSortable = Sortable.create(itemList.value, {
    animation: 150,
    handle: '.item-card-handle',
    filter: 'button, input, textarea',
    preventOnFilter: false,
    onEnd: () => {
      const uids = Array.from(itemList.value.querySelectorAll('[data-item-uid]'))
        .map((el) => el.dataset.itemUid)
        .filter(Boolean)
      if (uids.length) emit('reorder-items', uids)
    }
  })
}

// Mount on open and whenever the items list re-renders (e.g. add/remove).
watch(itemList, () => nextTick(mountItemSortable))
watch(() => props.block.content?.items?.length, () => nextTick(mountItemSortable))

onBeforeUnmount(() => itemSortable?.destroy())

const open = ref(false)

const items = computed(() => props.block.content?.items || [])
const itemCount = computed(() => items.value.length)

// On a repeatable section, schema fields marked `scope: "section"` are the
// section's own header copy (eyebrow / heading / intro) — edited once, not per
// item. Split them out so they render above the items list, and exclude them
// from the per-item field loop. Non-repeatable sections have no scope marker,
// so `itemSchema` there is just the full schema (unused) and `sectionSchema` empty.
function filterSchema(scopeSection) {
  const schema = props.block.section?.schema || {}
  return Object.fromEntries(Object.entries(schema).filter(([, defn]) => ((defn && defn.scope === 'section') === scopeSection)))
}
const sectionSchema = computed(() => filterSchema(true))
const itemSchema = computed(() => filterSchema(false))
const autoSourced = computed(() => !!props.block.section?.auto_source)
const autoSourceLabel = computed(() => {
  switch (props.block.section?.auto_source) {
    case 'services': return 'services'
    case 'service_areas': return 'locations'
    default: return props.block.section?.auto_source
  }
})

// For repeatable sections the importer stores defaults under
// `default_content.items[]`. When the customer adds a new item beyond the
// imported count, no default exists for that index — return empty so the
// "template default" hint stays off.
function defaultItemValue(index, fieldKey) {
  const defaults = props.block.section?.default_content?.items || []
  return defaults[index]?.[fieldKey] || ''
}

function onFieldUpdate(fieldKey, value) {
  const content = { ...(props.block.content || {}), [fieldKey]: value }
  // Second arg = live-patch hint so the parent can update the preview
  // element instantly (no reload). itemUid null for static sections.
  emit('update', content, { section: props.block.section?.key, field: fieldKey, value, itemUid: null })
}

// Keyboard-accessible item reorder: emit the new ordered uids (same payload
// SortableJS produces). No-op if items lack uids (can't reorder reliably).
function moveItem(index, dir) {
  const target = index + dir
  if (target < 0 || target >= items.value.length) return
  const uids = items.value.map((it) => it._uid)
  if (uids.some((u) => !u)) return
  const [moved] = uids.splice(index, 1)
  uids.splice(target, 0, moved)
  emit('reorder-items', uids)
}

function onItemFieldUpdate(index, fieldKey, value) {
  const item = items.value[index]
  const nextItems = items.value.map((it, i) => (i === index ? { ...it, [fieldKey]: value } : it))
  emit('update', { ...(props.block.content || {}), items: nextItems }, { section: props.block.section?.key, field: fieldKey, value, itemUid: item?._uid || null })
}

// Style override helpers — read/write nested element_overrides paths.
// Schema:
//   element_overrides = {
//     '<field>': { 'style': { 'color': '#...', 'background-color': '#...' } },
//     'items': { '<uid>': { 'fields': { '<field>': { 'style': {...} } } } }
//   }
function fieldStyle(fieldKey) {
  return props.block.element_overrides?.[fieldKey]?.style || null
}

function itemFieldStyle(uid, fieldKey) {
  if (!uid) return null
  return props.block.element_overrides?.items?.[uid]?.fields?.[fieldKey]?.style || null
}

function onFieldStyleUpdate(fieldKey, style) {
  const empty = !style || Object.keys(style).length === 0
  emit('update-overrides', {
    [fieldKey]: empty ? { style: null } : { style }
  })
}

function onItemFieldStyleUpdate(uid, fieldKey, style) {
  if (!uid) return
  const empty = !style || Object.keys(style).length === 0
  emit('update-overrides', {
    items: {
      [uid]: {
        fields: { [fieldKey]: empty ? { style: null } : { style } }
      }
    }
  })
}

// Parent-driven: opens the section and returns whether it was already open
// so the caller can avoid an unnecessary layout shift.
function expand() {
  const wasOpen = open.value
  open.value = true
  return wasOpen
}

defineExpose({ expand })
</script>
